import {
  Button,
  designTokens,
  Flex,
  Heading,
  InputDateRangev2,
  InputSelect,
  ItandiChip,
  Label,
  PaginationWithCounter,
  Panel,
  PanelBody,
  RiFilter2Fill,
  TextLink,
  YokuaruBody,
  YokuaruHeader,
  Input
} from '@itandi/itandi-bb-ui';
import { SingleComboBox } from '@itandi/itandi-bb-ui/SingleComboBox';
import { Table } from '@itandi/itandi-bb-ui/Table';
import { Tbody } from '@itandi/itandi-bb-ui/Tbody';
import { Thead } from '@itandi/itandi-bb-ui/Thead';
import { Th } from '@itandi/itandi-bb-ui/Th';
import { Tr } from '@itandi/itandi-bb-ui/Tr';
import { Td } from '@itandi/itandi-bb-ui/Td';
import Link from 'next/link';
import { useState, type ChangeEvent, type ComponentProps } from 'react';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import {
  isQueryCompleted,
  type TodoSortKey,
  type CompanyDealTodo,
  type CompanyDealTodoSearchCondition,
  staffOptions,
  selectedStaffOption
} from '~/models/CompanyDealTodo';
import { PATH as CLIENT_DEAL_PATH } from '~/pages/deals/[id]';
import { DealTypeChip } from '~/components/common/parts/chip/DealTypeChip';
import { truncate } from '~/utils/StringUtil';
import {
  formatDateWithWeekday,
  formatHyphenDate,
  toDate
} from '~/utils/DateUtil';
import { OriginVendorCompanyCell } from './OriginVendorCompanyCell';
import { TodoDueDateCell } from './TodoDueDateCell';
import { DealClientTodoModal } from '~/components/common/deals/show/main/todos/DealClientTodoModal';
import { type CompanyVendor } from '~/models/CompanyVendor';
import { STATUS_OPTIONS } from '~/models/DealTodo';
import type { Staff } from '~/models/Staff';
import { SearchDrawer } from './SearchDrawer';
import type { ItandiAccount } from '~/models/ItandiAccount';
import type { CompanyStoreSetting } from '~/models/CompanyStoreSetting';
import { StatusIcon } from '~/components/deprecated/StatusIcon';

type Props = {
  account: ItandiAccount;
  companyStoreSettings: CompanyStoreSetting[];
  totalItems: number;
  companyTodos: CompanyDealTodo[];
  staffs: Staff[];
  companyVendors: CompanyVendor[];
  currentPageNum: number;
  onSearchConditionChange: (condition: CompanyDealTodoSearchCondition) => void;
  searchCondition: CompanyDealTodoSearchCondition;
};

export const Page: React.FC<Props> = ({
  account,
  companyStoreSettings,
  totalItems,
  companyTodos,
  staffs,
  companyVendors,
  currentPageNum,
  onSearchConditionChange,
  searchCondition
}) => {
  const [editTodo, setEditTodo] = useState<CompanyDealTodo | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [dealName, setDealName] = useState(searchCondition.dealName ?? '');
  const [roomNumber, setRoomNumber] = useState(
    searchCondition.roomNumber ?? ''
  );

  const onClose = (): void => setIsDrawerOpen(false);
  const selectedStaff =
    staffs.find(({ id }) => id === searchCondition.staffId) ?? null;

  const onPageChange = async (newPageNumber: number): Promise<void> => {
    onSearchConditionChange({ page: newPageNumber });
  };

  const onStatusChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { value } = event.target;
    if (isQueryCompleted(value)) {
      onSearchConditionChange({ completed: value });
    }
  };

  const onStaffChange: ComponentProps<typeof SingleComboBox>['onChange'] = (
    option
  ): void => {
    onSearchConditionChange({ staffId: option && Number(option.value) });
  };

  const onDealNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDealName(e.target.value);
    onSearchConditionChange({ dealName: e.target.value });
  };

  const onRoomNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setRoomNumber(e.target.value);
    onSearchConditionChange({ roomNumber: e.target.value });
  };

  const handleDetailSearchSubmit = (
    condition: CompanyDealTodoSearchCondition
  ): void => {
    setDealName(condition.dealName ?? '');
    setRoomNumber(condition.roomNumber ?? '');
    onSearchConditionChange({ ...condition, page: 1 });
  };

  const onDueDateRangeChange = ([from, to]: [
    Date | null,
    Date | null
  ]): void => {
    const dueDateFrom = formatHyphenDate(from) ?? undefined;
    const dueDateTo = formatHyphenDate(to) ?? undefined;
    onSearchConditionChange({ dueDateFrom, dueDateTo });
  };

  const todoSortCondition = (
    key: TodoSortKey
  ): Exclude<CompanyDealTodoSearchCondition['sortOrder'], undefined> => {
    const { sortKey, sortOrder } = searchCondition;
    return sortKey === key && (sortOrder === 'asc' || sortOrder === 'desc')
      ? sortOrder
      : 'none';
  };

  const handleTodoSort =
    (key: TodoSortKey): (() => void) =>
    () => {
      const order = todoSortCondition(key);
      if (order === 'none') {
        onSearchConditionChange({ sortKey: key, sortOrder: 'desc' });
      } else if (order === 'desc') {
        onSearchConditionChange({ sortKey: key, sortOrder: 'asc' });
      } else {
        onSearchConditionChange({ sortKey: undefined, sortOrder: undefined });
      }
    };

  return (
    <DefaultV2>
      <YokuaruHeader heading={<Heading as="h1">TODO一覧</Heading>} />
      <YokuaruBody>
        <Panel>
          <PanelBody>
            <Flex
              alignItems="flex-end"
              flexWrap="wrap"
              gap={designTokens['spacing/m']}
            >
              <Flex direction="column" gap={designTokens['spacing/xxxs']}>
                <Label color="subtle">ステータス</Label>
                <InputSelect
                  onChange={onStatusChange}
                  value={searchCondition.completed}
                  width="10rem"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </InputSelect>
              </Flex>
              <Flex direction="column" gap={designTokens['spacing/xxxs']}>
                <Label color="subtle">担当者名</Label>
                <SingleComboBox
                  onChange={onStaffChange}
                  options={staffOptions(staffs, account.staff)}
                  placeholder="担当者を選択"
                  value={selectedStaffOption(searchCondition, selectedStaff)}
                  width="10rem"
                />
              </Flex>
              <Flex
                direction="column"
                gap={designTokens['spacing/xxxs']}
                width="17rem"
              >
                <Label color="subtle">物件名</Label>
                <Input
                  onChange={onDealNameChange}
                  placeholder="物件名を2文字以上入力してください"
                  value={dealName}
                />
              </Flex>
              <Flex
                direction="column"
                gap={designTokens['spacing/xxxs']}
                width="8rem"
              >
                <Label color="subtle">部屋番号</Label>
                <Input
                  onChange={onRoomNumberChange}
                  placeholder="101"
                  value={roomNumber}
                />
              </Flex>
              <Flex direction="column" gap={designTokens['spacing/xxxs']}>
                <Label color="subtle">対応期限</Label>
                <InputDateRangev2
                  fromDate={toDate(searchCondition.dueDateFrom ?? null)}
                  onChange={onDueDateRangeChange}
                  toDate={toDate(searchCondition.dueDateTo ?? null)}
                  width="16rem"
                />
              </Flex>
              <Button
                onClick={() => setIsDrawerOpen(true)}
                startIcon={<RiFilter2Fill />}
                variant="secondary"
              >
                詳細検索
              </Button>
            </Flex>
          </PanelBody>
        </Panel>
        <Panel>
          <PanelBody>
            <Table>
              <Thead>
                <Tr>
                  <Th width="6rem">ステータス</Th>
                  <Th width="20rem">案件</Th>
                  <Th width="15rem">タイトル</Th>
                  <Th width="30rem">メモ</Th>
                  <Th width="8rem">店舗</Th>
                  <Th width="7rem">担当者</Th>
                  <Th width="12rem">
                    施工会社
                    <br />
                    担当者
                  </Th>
                  <Th
                    onSort={handleTodoSort('id')}
                    sort={todoSortCondition('id')}
                    width="7rem"
                  >
                    登録日
                  </Th>
                  <Th
                    onSort={handleTodoSort('dueDate')}
                    sort={todoSortCondition('dueDate')}
                    width="8rem"
                  >
                    対応期限
                  </Th>
                  <Th width="7rem">完了日</Th>
                </Tr>
              </Thead>
              <Tbody>
                {companyTodos.map((companyTodo) => {
                  const { deal } = companyTodo;
                  const memo = companyTodo.memo ?? '';

                  return (
                    <Tr key={companyTodo.id}>
                      <Td>
                        <ItandiChip
                          label={companyTodo.completed ? '完了' : '未完了'}
                        />
                      </Td>
                      <Td>
                        <Flex
                          alignItems="start"
                          direction="column"
                          gap={designTokens['spacing/xxxs']}
                        >
                          <DealTypeChip
                            dealSubtype={deal.dealSubtype}
                            dealType={deal.dealType}
                          />
                          <TextLink
                            as={Link}
                            color="link"
                            href={CLIENT_DEAL_PATH(deal.id)}
                            target="_blank"
                          >
                            {`${truncate(deal.name, 30)} ${
                              deal.roomNumber ?? ''
                            }`}
                          </TextLink>
                        </Flex>
                      </Td>
                      <Td>
                        <TextLink
                          color="link"
                          onClick={() => setEditTodo(companyTodo)}
                        >
                          {truncate(companyTodo.title, 20)}
                        </TextLink>
                      </Td>
                      <Td>{truncate(memo, 75)}</Td>
                      <Td>
                        <Label>{companyTodo.storeName ?? ''}</Label>
                      </Td>
                      <Td>
                        <Label
                          color={
                            companyTodo.staff === null ? 'warning' : undefined
                          }
                          size="xs"
                        >
                          {truncate(companyTodo.staff?.name ?? '設定なし', 15)}
                        </Label>
                        {companyTodo.staff === null && (
                          <StatusIcon variant="Alert" />
                        )}
                      </Td>
                      <Td>
                        <OriginVendorCompanyCell
                          originVendorCompany={companyTodo.originVendorCompany}
                        />
                      </Td>
                      <Td>{formatDateWithWeekday(companyTodo.createdAt)}</Td>
                      <Td>
                        <TodoDueDateCell todoDueDate={companyTodo.dueDate} />
                      </Td>
                      <Td>
                        <Label
                          color={
                            companyTodo.completedAt === null
                              ? 'subtle'
                              : undefined
                          }
                          size={companyTodo.completedAt === null ? 'xs' : 's'}
                        >
                          {formatDateWithWeekday(companyTodo.completedAt) ??
                            '未完了'}
                        </Label>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </PanelBody>
          <Flex justifyContent="center" mt={designTokens['spacing/m']}>
            <PaginationWithCounter
              currentPageNum={currentPageNum}
              itemsPerPage={50}
              onPageChange={onPageChange}
              totalItems={totalItems}
            />
          </Flex>
        </Panel>
        {editTodo && (
          <DealClientTodoModal
            deal={editTodo.deal}
            handleClose={() => setEditTodo(null)}
            searchCondition={searchCondition}
            staffs={staffs}
            todo={editTodo}
          />
        )}
      </YokuaruBody>
      {isDrawerOpen && (
        <SearchDrawer
          account={account}
          companyStoreSettings={companyStoreSettings}
          companyVendors={companyVendors}
          onClose={onClose}
          onSubmit={handleDetailSearchSubmit}
          searchCondition={searchCondition}
          staffs={staffs}
        />
      )}
    </DefaultV2>
  );
};
